/**
 * =========================================
 * AUTHENTICATION CONTEXT
 * =========================================
 *
 * Provides authentication state management and methods throughout the application.
 */

import React, { createContext, useContext, useState, useEffect, useMemo, useRef } from 'react';
import { http } from '../lib/http';
import {
  isProfileEmailVerified,
  markPendingEmailVerification,
  clearPendingEmailVerification,
  clearVerifyForBooking,
} from '../utils/emailVerification';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

function buildUserFromLoginResponse(data) {
  const emailVerified = data.emailVerified ?? data.user?.emailVerified;
  return {
    ...(data.user || {}),
    role: data.user?.role || 'client',
    emailVerified:
      emailVerified === true ||
      Boolean(data.user?.isEmailVerified || data.user?.isPhoneVerified),
  };
}

function userFromAccessToken(token) {
  if (!token) return null;
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json);
    if (!payload?._id) return null;
    return {
      _id: payload._id,
      id: payload._id,
      role: payload.role || 'client',
      emailVerified: false,
    };
  } catch {
    return null;
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  /** Bumped on login/logout so in-flight checkAuthStatus cannot wipe a fresh session */
  const authEpochRef = useRef(0);
  const userRef = useRef(null);
  userRef.current = user;

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    const epoch = authEpochRef.current;

    try {
      setIsLoading(true);

      const token = http.getToken();
      if (!token) {
        if (epoch === authEpochRef.current && !userRef.current) {
          setUser(null);
          setIsAuthenticated(false);
        }
        return;
      }

      const profile = await http.get('/api/profile');
      if (epoch !== authEpochRef.current) return;

      const userPayload = {
        ...profile,
        role: profile.role || 'client',
        emailVerified: isProfileEmailVerified(profile),
      };
      setUser(userPayload);
      setIsAuthenticated(true);
    } catch (error) {
      if (epoch !== authEpochRef.current) return;

      console.log('Auth check failed:', error.message);

      if (error.status === 401) {
        try {
          const refreshed = await http.attemptRefresh();
          if (epoch !== authEpochRef.current) return;

          if (refreshed) {
            const profile = await http.get('/api/profile');
            if (epoch !== authEpochRef.current) return;

            const userPayload = {
              ...profile,
              role: profile.role || 'client',
              emailVerified: isProfileEmailVerified(profile),
            };
            setUser(userPayload);
            setIsAuthenticated(true);
            return;
          }
        } catch (refreshError) {
          console.log('Token refresh failed:', refreshError.message);
        }

        if (epoch !== authEpochRef.current) return;
        // Background auth check must not destroy an active login session
        if (userRef.current && http.getToken()) {
          setIsAuthenticated(true);
          return;
        }
        http.clearToken();
        setUser(null);
        setIsAuthenticated(false);
        return;
      }

      // Profile/network errors must never log out a valid token session
      if (http.getToken()) {
        setIsAuthenticated(true);
        if (!userRef.current) {
          const fromToken = userFromAccessToken(http.getToken());
          if (fromToken) setUser(fromToken);
        }
        return;
      }
    } finally {
      if (epoch === authEpochRef.current) {
        setIsLoading(false);
      }
    }
  };

  const login = async (email, password, rememberMe = false) => {
    try {
      authEpochRef.current += 1;

      http.clearToken();

      const data = await http.post('/login', {
        email: email.toLowerCase().trim(),
        password,
      });

      http.setToken(data.accessToken, { remember: rememberMe });

      let userPayload;
      try {
        const profile = await http.get('/api/profile');
        userPayload = {
          ...profile,
          role: profile.role || data.user?.role || 'client',
          emailVerified: isProfileEmailVerified(profile),
        };
      } catch {
        userPayload = buildUserFromLoginResponse(data);
      }

      if (userPayload.emailVerified === false) {
        markPendingEmailVerification(userPayload.email);
      } else {
        clearPendingEmailVerification();
        clearVerifyForBooking();
      }

      setUser(userPayload);
      setIsAuthenticated(true);
      setIsLoading(false);

      return { success: true, user: userPayload, emailVerified: userPayload.emailVerified };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Login failed',
      };
    }
  };

  const register = async (userData) => {
    try {
      const data = await http.post('/register', userData);

      if (data.accessToken) {
        authEpochRef.current += 1;
        http.setToken(data.accessToken, { remember: true });
      }

      const userPayload = {
        ...(data.user || data),
        role: data.user?.role || data.role || 'client',
      };
      setUser(userPayload);
      setIsAuthenticated(Boolean(data.accessToken));
      setIsLoading(false);

      return { success: true, user: userPayload };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Registration failed',
      };
    }
  };

  const logout = () => {
    authEpochRef.current += 1;
    http.clearToken();
    setUser(null);
    setIsAuthenticated(false);
    setIsLoading(false);
  };

  const refreshAuth = async () => {
    await checkAuthStatus();
  };

  const value = useMemo(
    () => ({
      user,
      isAuthenticated,
      isLoading,
      login,
      register,
      logout,
      refreshAuth,
    }),
    [user, isAuthenticated, isLoading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
